import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';

/**
 * Toast notification component
 *
 * A notification toast that appears at the top-center of the screen.
 * Auto-dismisses after a specified duration.
 *
 * @param {Object} props
 * @param {string} props.message - Notification message (if falsy, toast won't show)
 * @param {string} [props.type='info'] - Toast type ('success', 'error', 'warning', 'info')
 * @param {Function} props.onClose - Close handler function
 * @param {number} [props.duration=6000] - Auto-dismiss duration in ms (0 = no auto-dismiss)
 *
 * @example
 * <Toast
 *   message={errorMessage}
 *   type="error"
 *   onClose={() => setErrorMessage(null)}
 * />
 */
export function Toast({
  message,
  type = 'info',
  onClose,
  duration = 6000,
}) {
  const isOpen = Boolean(message);

  // Auto-dismiss after duration
  useEffect(() => {
    if (isOpen && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isOpen, duration, onClose]);

  if (!isOpen) return null;

  // Type configurations
  const typeConfig = {
    success: {
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      iconColor: 'text-green-600',
      icon: 'lucide:check-circle',
    },
    error: {
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      iconColor: 'text-red-600',
      icon: 'lucide:alert-circle',
    },
    warning: {
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-800',
      iconColor: 'text-yellow-600',
      icon: 'lucide:alert-triangle',
    },
    info: {
      bgColor: 'bg-app',
      borderColor: 'border-brand',
      textColor: 'text-brand',
      iconColor: 'text-brand',
      icon: 'lucide:info',
    },
  };

  const config = typeConfig[type];

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[80] w-full max-w-md px-4 animate-fadeIn">
      <div
        className={`flex items-start gap-3 p-4 rounded-lg border ${config.bgColor} ${config.borderColor} shadow-lg`}
        role="alert"
      >
        {/* Icon */}
        <Icon
          icon={config.icon}
          className={`shrink-0 size-5 ${config.iconColor} mt-0.5`}
        />

        {/* Message */}
        <div className={`flex-1 text-sm font-medium ${config.textColor}`}>
          {message}
        </div>

        {/* Close Button */}
        <button
          type="button"
          className={`shrink-0 inline-flex justify-center items-center size-5 rounded-md cursor-pointer ${config.textColor} hover:bg-white/50 focus:outline-none focus:bg-white/50`}
          onClick={onClose}
          aria-label="Close"
        >
          <Icon icon="lucide:x" className="size-4" />
        </button>
      </div>
    </div>
  );
}

Toast.propTypes = {
  message: PropTypes.string,
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  onClose: PropTypes.func.isRequired,
  duration: PropTypes.number,
};
